import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, m, useIsPresent } from "motion/react";
import { LogOut, Trash2, Wallet } from "lucide-react";
import { LinhaViva, NumeroAnimado, useMovimento, useOrdemDeEntrada, useSentido } from "@vertex/ui/movimento";
import {
  brl,
  lineTotal,
  precoEfetivo,
  qtyErrorForUnit,
  roundMoney,
  roundQty,
  splitQtyPrefix,
  unitShort,
  type PdvCartLine,
  type PdvCheckoutResult,
  type PdvVariant,
} from "@vertex/validators/pdv-pure";
import { buttonClass } from "@vertex/ui/primitives/button-style";
import { EmptyState } from "@vertex/ui/primitives/empty-state";
import { SearchInput } from "@vertex/ui/primitives/search-input";
import { useKeyScope } from "../keys/registry";
import { ApiError, newClientRef } from "../lib/api";
import { pdvApi, type PdvContext } from "../lib/pdv";
import { useCatalog } from "../lib/use-catalog";
import { useToast } from "../lib/toast";
import { PaymentPanel } from "./payment";
import { Receipt } from "./receipt";

/** Linha do carrinho com o que a TELA precisa além do que o servidor recebe. */
export interface CartItem extends PdvCartLine {
  variant: PdvVariant;
  /** Preço unitário efetivo desta linha (promoção ou digitado pelo operador). */
  price: number;
}

export function SaleScreen({
  ctx,
  onOpenRegister,
  onLogout,
  onSold,
  onHelp,
  registerScan,
}: {
  ctx: PdvContext;
  onOpenRegister: () => void;
  onLogout: () => void;
  onSold: () => void;
  onHelp: () => void;
  /** Publica no provider o "como adicionar" que o wedge vai disparar. */
  registerScan: (fn: (code: string, qty: number) => void) => void;
}) {
  const notify = useToast();
  /* VOS Movimento (fase 4): a linha bipada entra e acende, quantidade e
     total ROLAM, a vitrine chega na cascata do sistema, o pagamento abre e
     fecha como sobreposição e o recibo entra de lado (a venda é a "casa":
     sair dela anda pra direita, voltar pra ela vem da esquerda). Nada disso
     segura o operador: o carrinho e o total já valem no quadro do bipe. */
  const mov = useMovimento();
  /* Saindo (o caixa abriu por cima), a venda já não responde a tecla: os
     140ms da saída não podem fechar venda nem limpar carrinho. */
  const presente = useIsPresent();
  const { buscar, porCodigo, fonte } = useCatalog();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<PdvVariant[]>([]);
  const [searching, setSearching] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [discount, setDiscount] = useState(0);
  const [paying, setPaying] = useState(false);
  const [sold, setSold] = useState<PdvCheckoutResult | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /**
   * `clientRef` nasce com o CARRINHO, não com o clique em "Finalizar". É o que
   * torna o retry idempotente: dois cliques, ou um timeout seguido de nova
   * tentativa, mandam a mesma chave e o servidor devolve a MESMA venda em vez
   * de criar a segunda. Só zera quando o carrinho zera.
   */
  const clientRef = useRef(newClientRef());

  // ── Busca com debounce. 180 ms: abaixo disso o leitor de código de barras
  // (que "digita" o EAN inteiro em ~50 ms) dispararia uma busca por prefixo.
  useEffect(() => {
    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await buscar(term, ac.signal));
      } catch (e) {
        if (!ac.signal.aborted) notify((e as Error).message, "error");
      } finally {
        if (!ac.signal.aborted) setSearching(false);
      }
    }, 180);
    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [term, notify, buscar]);

  const addLine = useCallback(
    (v: PdvVariant, qty = 1) => {
      const erro = qtyErrorForUnit(qty, v.unit);
      if (erro) {
        notify(erro, "warning");
        return;
      }
      setCart((prev) => {
        const i = prev.findIndex((l) => l.variantId === v.id);
        if (i >= 0) {
          const next = [...prev];
          next[i] = { ...next[i]!, qty: roundQty(next[i]!.qty + qty) };
          return next;
        }
        return [
          ...prev,
          {
            kind: "product",
            variantId: v.id,
            qty: roundQty(qty),
            fulfillment: "now",
            variant: v,
            price: precoEfetivo(v),
          },
        ];
      });
      setTerm("");
      searchRef.current?.focus();
    },
    [notify],
  );

  /**
   * Enter na busca. Duas coisas acontecem aqui e as duas vêm do balcão real:
   * o padrão `3*789` (três daquele código, sem tirar a mão do numérico) e a
   * bipagem, que chega como texto seguido de Enter. Código exato — SKU ou
   * código de barras — entra direto; senão, cai no primeiro resultado.
   */
  const onSearchEnter = useCallback(() => {
    const { qty, code } = splitQtyPrefix(term);
    if (!code) return;
    const alvo =
      results.find((v) => v.sku === code || v.barcode === code) ??
      (results.length === 1 ? results[0] : undefined) ??
      results[0];
    if (!alvo) {
      notify(`Nada encontrado para "${code}"`, "warning");
      return;
    }
    addLine(alvo, qty);
  }, [term, results, addLine, notify]);

  /**
   * Bipe. Busca pelo código EXATO — barcode ou SKU — e não pela busca
   * incremental: um EAN que casa por prefixo com outro produto adicionaria o
   * item errado, e no balcão isso vira divergência de estoque que ninguém
   * consegue reconstituir depois.
   */
  const aoBipar = useCallback(
    async (code: string, qty: number) => {
      /* O que já está na tela primeiro: bipar o mesmo item duas vezes seguidas
         não deveria custar nem uma consulta ao banco. */
      const naTela = results.find((v) => v.barcode === code || v.sku === code);
      if (naTela) {
        addLine(naTela, qty);
        return;
      }
      try {
        const exato = await porCodigo(code);
        if (exato) addLine(exato, qty);
        else notify(`Código ${code} não encontrado`, "warning");
      } catch (e) {
        notify((e as Error).message, "error");
      }
    },
    [results, addLine, notify, porCodigo],
  );

  useEffect(() => {
    registerScan((code, qty) => void aoBipar(code, qty));
  }, [registerScan, aoBipar]);

  const setQty = useCallback((variantId: string, qty: number) => {
    setCart((prev) =>
      qty <= 0
        ? prev.filter((l) => l.variantId !== variantId)
        : prev.map((l) => (l.variantId === variantId ? { ...l, qty: roundQty(qty) } : l)),
    );
  }, []);

  const subtotal = useMemo(
    () => roundMoney(cart.reduce((acc, l) => acc + lineTotal(l.qty, l.price), 0)),
    [cart],
  );
  /* Desconto nunca deixa o total negativo: o operador digita 100 num carrinho
     de 80 e a venda viraria devolução silenciosa. */
  const descontoEfetivo = Math.min(roundMoney(discount), subtotal);
  const total = roundMoney(subtotal - descontoEfetivo);

  /* A FORMA do carrinho: é só quando ela muda (entrou, saiu, a quantidade
     mudou) que as linhas se medem pra andar. Digitar na busca re-renderiza a
     tela inteira e não pode medir o carrinho a cada tecla. */
  const formaDoCarrinho = cart.map((l) => `${l.variantId}:${l.qty}`).join("|");
  const chegadaNaVitrine = useOrdemDeEntrada(results.map((v) => v.id));
  const sentidoDaTela = useSentido(sold ? 1 : 0);

  const limpar = useCallback(() => {
    setCart([]);
    setDiscount(0);
    clientRef.current = newClientRef();
    searchRef.current?.focus();
  }, []);

  const finalizar = useCallback(
    async (paymentMethod: "cash" | "pix" | "card") => {
      try {
        const res = await pdvApi.checkout({
          clientRef: clientRef.current,
          items: cart.map(({ kind, variantId, qty, fulfillment, price }) => ({
            kind,
            variantId,
            qty,
            fulfillment,
            unitPrice: price,
          })),
          paymentMode: "now",
          discount: descontoEfetivo,
          paymentMethod,
        });
        setSold(res);
        setPaying(false);
        setCart([]);
        setDiscount(0);
        clientRef.current = newClientRef();
        onSold();
        if (res.replayed) notify("Esta venda já tinha sido registrada.", "info");
      } catch (e) {
        const err = e as ApiError;
        notify(err.message, "error");
        /* Erro transitório mantém o carrinho E o `clientRef`: tentar de novo
           tem que reaproveitar a mesma chave, senão a venda duplica quando a
           primeira tentativa tiver chegado ao servidor apesar do timeout. */
        if (!(err instanceof ApiError) || !err.transient) clientRef.current = newClientRef();
      }
    },
    [cart, descontoEfetivo, notify, onSold],
  );

  /**
   * Escopo de teclas da venda. Fica no fundo da pilha (o pagamento e a ajuda
   * empurram os deles por cima), então estas teclas só respondem quando não há
   * nada aberto — que é exatamente o esperado: F3 não pode abrir um segundo
   * checkout sobre o primeiro.
   */
  useKeyScope(
    "sale",
    {
      help: () => {
        onHelp();
        return true;
      },
      search: () => {
        searchRef.current?.focus();
        searchRef.current?.select();
        return true;
      },
      checkout: () => {
        if (cart.length === 0) {
          notify("O carrinho está vazio.", "warning");
          return true;
        }
        setPaying(true);
        return true;
      },
      discount: () => {
        document.getElementById("pdv-desc")?.focus();
        return true;
      },
      void_line: () => {
        /* Remove a ÚLTIMA linha, que é a que o operador acabou de errar. Pedir
           pra selecionar antes seria um passo a mais no momento mais apressado
           da venda. */
        const ultima = cart[cart.length - 1];
        if (!ultima) return true;
        setQty(ultima.variantId!, 0);
        notify(`Removido: ${ultima.variant.productName}`, "info");
        return true;
      },
      void_sale: () => {
        if (cart.length === 0) return true;
        limpar();
        notify("Venda cancelada.", "info");
        return true;
      },
      register: () => {
        onOpenRegister();
        return true;
      },
      cash_move: () => {
        onOpenRegister();
        return true;
      },
      back: () => {
        if (term) {
          setTerm("");
          return true;
        }
        return false;
      },
    },
    !paying && !sold && presente,
  );

  const vista = mov.r.vista();
  return (
    <AnimatePresence initial={false} mode="popLayout" custom={sentidoDaTela}>
      {sold ? (
        <m.div
          key="recibo"
          className="h-full"
          custom={sentidoDaTela}
          variants={vista}
          initial="entra"
          animate="fica"
          exit="sai"
        >
          <Receipt
            result={sold}
            ctx={ctx}
            onNewSale={() => {
              setSold(null);
              searchRef.current?.focus();
            }}
          />
        </m.div>
      ) : (
    <m.div
      key="venda"
      className="grid h-full grid-cols-[1fr_380px]"
      custom={sentidoDaTela}
      variants={vista}
      initial="entra"
      animate="fica"
      exit="sai"
    >
      {/* ── Catálogo ── */}
      <section className="flex min-w-0 flex-col p-4">
        <header className="mb-3 flex items-center gap-3">
          <SearchInput
            ref={searchRef}
            autoFocus
            aria-label="Buscar produto por nome, SKU ou código de barras"
            placeholder="Bipe o código ou busque pelo nome…   3*789 = três daquele"
            className="flex-1"
            loading={searching}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onClear={() => setTerm("")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSearchEnter();
              }
            }}
          />
          <button
            type="button"
            className={buttonClass("secondary", "md")}
            onClick={onOpenRegister}
          >
            <Wallet size={16} aria-hidden />
            <span className="ml-2">Caixa</span>
          </button>
          <button
            type="button"
            className={buttonClass("ghost", "md")}
            onClick={onLogout}
            aria-label="Sair do terminal"
          >
            <LogOut size={16} aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto">
          {results.length === 0 && !searching ? (
            <EmptyState
              title={term ? "Nada encontrado" : "Comece a vender"}
              description={
                term
                  ? "Confira o código ou busque por outro termo."
                  : "Bipe o código de barras ou digite o nome do produto."
              }
            />
          ) : (
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-2">
              {/* A presença própria da vitrine NÃO é enfeite: sem ela o card
                  herdaria o `initial={false}` da presença da tela (a venda
                  estava lá na 1ª pintura) e nasceria parado. Quem sai, sai na
                  hora (não tem `exit`): a resposta nova não espera a velha. */}
              <AnimatePresence>
              {results.map((v) => {
                const vez = chegadaNaVitrine.get(v.id);
                const r = mov.r.item(vez ?? 0);
                return (
                <m.li
                  key={v.id}
                  initial={vez === undefined ? false : r.initial}
                  animate={r.animate}
                  whileTap={mov.r.apertoCard}
                >
                  <button
                    type="button"
                    onClick={() => addLine(v)}
                    className="pdv-produto flex h-full w-full flex-col gap-1 rounded-xl p-3 text-left"
                  >
                    <span className="line-clamp-2 font-semibold" style={{ fontSize: "var(--fs-sm)" }}>
                      {v.name ? `${v.productName} · ${v.name}` : v.productName}
                    </span>
                    <span style={{ fontSize: "var(--fs-caption)", color: "var(--ink-3)" }}>
                      {v.sku}
                      {v.trackStock && v.stock !== null
                        ? v.allowBackorder && v.stock <= 0
                          ? " · sob encomenda"
                          : ` · ${v.stock} em estoque`
                        : ""}
                    </span>
                    <strong className="mt-auto" style={{ fontSize: "var(--fs-body)" }}>
                      {brl(precoEfetivo(v))}
                      <span style={{ fontSize: "var(--fs-caption)", color: "var(--ink-3)" }}>
                        {` /${unitShort(v.unit)}`}
                      </span>
                    </strong>
                  </button>
                </m.li>
                );
              })}
              </AnimatePresence>
            </ul>
          )}
        </div>
      </section>

      {/* ── Carrinho ── */}
      <aside
        className="flex min-h-0 flex-col"
        style={{ background: "var(--panel)", borderLeft: "1px solid var(--line)" }}
      >
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--line)" }}>
          <strong style={{ fontSize: "var(--fs-sm)" }}>
            {ctx.pdv.code}
            {ctx.pdv.unitName ? ` · ${ctx.pdv.unitName}` : ""}
            {/* Catálogo local ativo. Vale o pixel: quando o balcão reclamar que
                "não achou o produto novo", esta é a primeira pergunta — o
                espelho pode estar desatualizado. */}
            {fonte === "local" ? (
              <span style={{ fontSize: "var(--fs-caption)", color: "var(--ok)" }}> · catálogo local</span>
            ) : null}
          </strong>
          {cart.length > 0 ? (
            <button type="button" className={buttonClass("ghost", "sm")} onClick={limpar}>
              <Trash2 size={14} aria-hidden />
              <span className="ml-1.5">Limpar</span>
            </button>
          ) : null}
        </div>

        {/* A linha bipada entra subindo 8px e ACENDE; bipar de novo o mesmo
            item acende de novo e a quantidade rola; a que sai encolhe fora do
            fluxo (`popLayout`) e as de baixo sobem pela mola `layout`. */}
        <ul className="relative min-h-0 flex-1 overflow-auto">
          <AnimatePresence initial={false} mode="popLayout">
          {cart.map((l) => (
            <LinhaViva
              key={l.variantId}
              como="li"
              chegou
              qtd={l.qty}
              dependencia={formaDoCarrinho}
              className="relative flex items-center gap-2 px-4 py-2.5"
              style={{ borderBottom: "1px solid var(--line)" }}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate" style={{ fontSize: "var(--fs-meta)" }}>
                  {l.variant.name ? `${l.variant.productName} · ${l.variant.name}` : l.variant.productName}
                </div>
                <div style={{ fontSize: "var(--fs-caption)", color: "var(--ink-3)" }}>
                  {`${l.qty} ${unitShort(l.variant.unit)} × ${brl(l.price)}`}
                </div>
              </div>
              <input
                type="number"
                aria-label={`Quantidade de ${l.variant.productName}`}
                className="h-9 w-[72px] rounded-lg px-2 text-right"
                style={{ background: "var(--field-bg)", border: "1px solid var(--line-2)", color: "var(--ink)" }}
                value={l.qty}
                min={0}
                step={l.variant.unit && l.variant.unit !== "UN" ? 0.001 : 1}
                onChange={(e) => setQty(l.variantId!, Number(e.target.value))}
              />
              <strong className="w-[92px] text-right" style={{ fontSize: "var(--fs-meta)" }}>
                <NumeroAnimado valor={lineTotal(l.qty, l.price)} formatar={brl} />
              </strong>
            </LinhaViva>
          ))}
          {cart.length === 0 ? (
            <m.li
              key="vazio"
              className="px-4 py-10 text-center"
              style={{ fontSize: "var(--fs-meta)", color: "var(--ink-3)" }}
              initial={mov.r.chegada.initial}
              animate={mov.r.chegada.animate}
              exit={{ opacity: 0, transition: mov.tempo("xs", "saida") }}
            >
              O carrinho está vazio.
            </m.li>
          ) : null}
          </AnimatePresence>
        </ul>

        <div className="px-4 py-3" style={{ borderTop: "1px solid var(--line)" }}>
          <div className="mb-2 flex items-center justify-between" style={{ fontSize: "var(--fs-meta)", color: "var(--ink-2)" }}>
            <span>Subtotal</span>
            <NumeroAnimado valor={subtotal} formatar={brl} />
          </div>
          <div className="mb-3 flex items-center justify-between gap-2" style={{ fontSize: "var(--fs-meta)", color: "var(--ink-2)" }}>
            <label htmlFor="pdv-desc">Desconto</label>
            <input
              id="pdv-desc"
              type="number"
              className="h-9 w-[110px] rounded-lg px-2 text-right"
              style={{ background: "var(--field-bg)", border: "1px solid var(--line-2)", color: "var(--ink)" }}
              min={0}
              step={0.01}
              value={discount || ""}
              onChange={(e) => setDiscount(Number(e.target.value) || 0)}
            />
          </div>
          <div className="mb-3 flex items-baseline justify-between">
            <span style={{ fontSize: "var(--fs-sm)" }}>Total</span>
            <strong style={{ fontSize: "var(--fs-kpi)", letterSpacing: "-0.02em" }}>
              <NumeroAnimado valor={total} formatar={brl} />
            </strong>
          </div>
          <button
            type="button"
            disabled={cart.length === 0}
            className={`${buttonClass("primary", "lg")} w-full`}
            onClick={() => setPaying(true)}
          >
            Finalizar Venda
          </button>
          {!ctx.register.open ? (
            <p className="mt-2 text-center" style={{ fontSize: "var(--fs-caption)", color: "var(--warn)" }}>
              O caixa está fechado. A venda em dinheiro não entra na gaveta.
            </p>
          ) : null}
        </div>
      </aside>

      {/* O pagamento abre e fecha como sobreposição (o véu esmaece, o painel
          cresce de .97): a presença deixa o Esc fechar com saída. */}
      <AnimatePresence>
        {paying ? (
          <PaymentPanel
            key="pagamento"
            total={total}
            ctx={ctx}
            onCancel={() => setPaying(false)}
            onConfirm={finalizar}
          />
        ) : null}
      </AnimatePresence>
    </m.div>
      )}
    </AnimatePresence>
  );
}
